import React, { createContext, useContext, useState, ReactNode } from 'react';

interface MapState {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
}

interface MapGLContextType {
  mapState: MapState;
  setMapState: (state: Partial<MapState>) => void;
  resetMap: () => void;
  flyTo: (center: [number, number], zoom?: number) => void;
}

const MapGLContext = createContext<MapGLContextType | undefined>(undefined);

interface MapGLProviderProps {
  children: ReactNode;
}

export const MapGLProvider: React.FC<MapGLProviderProps> = ({ children }) => {
  const [mapState, setMapState] = useState<MapState>({
    center: [28.9784, 41.0082], // Istanbul coordinates
    zoom: 10,
    bearing: 0,
    pitch: 0,
  });

  const updateMapState = (newState: Partial<MapState>) => {
    setMapState(prev => ({ ...prev, ...newState }));
  };

  const resetMap = () => {
    setMapState({
      center: [28.9784, 41.0082],
      zoom: 10,
      bearing: 0,
      pitch: 0,
    });
  };

  const flyTo = (center: [number, number], zoom: number = 10) => {
    setMapState(prev => ({
      ...prev,
      center,
      zoom,
    }));
  };

  const value: MapGLContextType = {
    mapState,
    setMapState: updateMapState,
    resetMap,
    flyTo,
  };

  return (
    <MapGLContext.Provider value={value}>
      {children}
    </MapGLContext.Provider>
  );
};

export const useMapGL = (): MapGLContextType => {
  const context = useContext(MapGLContext);
  if (context === undefined) {
    throw new Error('useMapGL must be used within a MapGLProvider');
  }
  return context;
}; 